/**
 * 智慧无限 DK87 / CX87-3M 键盘矩阵：6 行 × 17 列，与固件 key_matrix_init_tab 一致。
 * 页面矩阵自绘（键宽/占位）；键码主路径 0x17 → 失败 0x16 → 本地出厂快照。
 */

import {
  resolveZhwxConsumerUiKeyCode,
  resolveZhwxConsumerUiKeyCodeFromCell,
  resolveZhwxFnFuncDisplayKeyCode,
  ZHWX_CON_TYPE,
  ZHWX_FUNC_TYPE,
} from "./zhwxFnDisplay";
import {
  ZHWX_KB_TYPE,
  ZHWX_MACRO_1_TYPE,
  ZHWX_MACRO_GUN_TYPE,
  ZHWX_MACRO_N_TYPE,
} from "./zhwxKeyEncode";
import { ZHWX_MACRO_SLOT_COUNT, ZHWX_MACRO_UI_BASE } from "./zhwxMacro";

export const ZHWX_MATRIX_COLS = 17;
export const ZHWX_MATRIX_ROWS = 6;

/** led_define_tab 字节数（6×17×3） */
export const ZHWX_LED_DEFINE_TAB_SIZE = 384;

export type ZhwxMatrixCell = {
  type: number;
  codeL: number;
  codeH: number;
};

export function zhwxMatrixIndex(row: number, col: number): number {
  return row * ZHWX_MATRIX_COLS + col;
}

/**
 * DK87 为 ANSI 配列：固件矩阵下列位置有占位键码，但实体键盘无键帽。
 * - (3,12) Caps 行 K29：`\|` 已在 Q 行，Enter 左侧为矩阵空槽
 * - (4,1) Shift 行 K45：LShift 与 Z 之间无 ISO 键（勿显示 Nubs）
 */
export const ZHWX_DK87_NON_PHYSICAL_CELLS: ReadonlyArray<
  readonly [number, number]
> = [
  [3, 12],
  [4, 1],
];

/** 该矩阵坐标在 DK87 上是否有实体键帽 */
export function isZhwxDk87PhysicalCell(row: number, col: number): boolean {
  return !ZHWX_DK87_NON_PHYSICAL_CELLS.some(([r, c]) => r === row && c === col);
}

/** 宏类型矩阵格：M0 等为 type=0x50 且 codeL=0，不能仅用 codeL/codeH 判断 */
function isZhwxMacroMatrixCell(cell: ZhwxMatrixCell): boolean {
  return (
    cell.type === ZHWX_MACRO_1_TYPE ||
    cell.type === ZHWX_MACRO_N_TYPE ||
    cell.type === ZHWX_MACRO_GUN_TYPE
  );
}

/** 矩阵格是否对应实体按键（空槽 type=KB 且 codeL/codeH 均为 0） */
export function isZhwxMatrixCellOccupied(cell: ZhwxMatrixCell): boolean {
  if (isZhwxMacroMatrixCell(cell)) return true;
  return cell.codeL !== 0 || cell.codeH !== 0;
}

/** DK87 展示/改键：既有键码又存在实体键帽 */
export function isZhwxDk87DisplayCell(
  row: number,
  col: number,
  cell: ZhwxMatrixCell
): boolean {
  return isZhwxMatrixCellOccupied(cell) && isZhwxDk87PhysicalCell(row, col);
}

export function parseZhwxMatrixCell(
  matrix: Uint8Array,
  index: number
): ZhwxMatrixCell {
  const base = index * 3;
  return {
    type: matrix[base] ?? 0,
    codeL: matrix[base + 1] ?? 0,
    codeH: matrix[base + 2] ?? 0,
  };
}

/** 从协议 3 字节单元解析原始键码（修饰键在 CODE_L，普通键在 CODE_H） */
export function zhwxMatrixCellToKeyCode(cell: ZhwxMatrixCell): number {
  if (cell.codeH) return cell.codeH;
  if (cell.codeL) return cell.codeL;
  return 0;
}

/** 系统类按键（keyboard_key_define.h SYS_TYPE） */
const ZHWX_SYS_TYPE = 0x30;

/** 协议热键位掩码 → 虚拟键盘 HID（与 KEYCODE_MAP 224~231 对齐） */
const ZHWX_HOTKEY_TO_UI_HID: Readonly<Record<number, number>> = {
  0x01: 224, // L-Ctrl
  0x02: 225, // L-Shift
  0x04: 226, // L-Alt
  0x08: 227, // L-Win
  0x10: 228, // R-Ctrl
  0x20: 229, // R-Shift
  0x40: 230, // R-Alt
  0x80: 231, // R-Win
};

/** KB_TYPE / 未标注 type：热键位在 codeL，普通键在 codeH */
function zhwxMatrixKbCellToUiKeyCode(cell: ZhwxMatrixCell): number {
  if (!cell.codeH && cell.codeL) {
    const modifier = ZHWX_HOTKEY_TO_UI_HID[cell.codeL];
    if (modifier != null) return modifier;
  }
  const raw = zhwxMatrixCellToKeyCode(cell);
  return raw ? resolveZhwxConsumerUiKeyCode(raw) : 0;
}

/**
 * 协议矩阵单元 → 虚拟键盘 UI 键码。
 * 普通键仍为 USB HID；修饰键从热键位掩码转为 224~231；FUNC_TYPE 走功能键展示码。
 */
export function zhwxMatrixCellToUiKeyCode(cell: ZhwxMatrixCell): number {
  if (isZhwxMacroMatrixCell(cell)) {
    const slot = cell.codeL;
    if (slot >= 0 && slot < ZHWX_MACRO_SLOT_COUNT) {
      return ZHWX_MACRO_UI_BASE + slot;
    }
  }
  if (cell.type === ZHWX_KB_TYPE || cell.type === 0x11 || cell.type === 0x00) {
    return zhwxMatrixKbCellToUiKeyCode(cell);
  }
  if (cell.type === ZHWX_FUNC_TYPE) {
    // 旧版误将宏槽写入 FUNC G 区：codeL=0x51+槽位
    if (
      cell.codeL >= 0x51 &&
      cell.codeL < 0x51 + ZHWX_MACRO_SLOT_COUNT &&
      !cell.codeH
    ) {
      return ZHWX_MACRO_UI_BASE + (cell.codeL - 0x51);
    }
    return resolveZhwxFnFuncDisplayKeyCode(cell.codeL, cell.codeH);
  }
  if (cell.type === ZHWX_CON_TYPE || cell.type === ZHWX_SYS_TYPE) {
    const ui = resolveZhwxConsumerUiKeyCodeFromCell(cell.codeL, cell.codeH);
    if (ui) return ui;
    return zhwxMatrixKbCellToUiKeyCode(cell);
  }
  return zhwxMatrixKbCellToUiKeyCode(cell);
}

/** 统计 DK87 矩阵解析后可展示的非零 UI 键数 */
export function countDk87UiKeysInMatrix(matrix: Uint8Array): number {
  let count = 0;
  for (let row = 0; row < ZHWX_MATRIX_ROWS; row++) {
    for (let col = 0; col < ZHWX_MATRIX_COLS; col++) {
      const cell = parseZhwxMatrixCell(matrix, zhwxMatrixIndex(row, col));
      if (!isZhwxDk87DisplayCell(row, col, cell)) continue;
      if (zhwxMatrixCellToUiKeyCode(cell)) count++;
    }
  }
  return count;
}

/** 由协议默认/当前矩阵生成 6×17 键码表（供静态兜底或对比） */
export function buildDk87KeycodeGridFromMatrix(matrix: Uint8Array): number[][] {
  const grid: number[][] = [];
  for (let row = 0; row < ZHWX_MATRIX_ROWS; row++) {
    const rowCodes: number[] = [];
    for (let col = 0; col < ZHWX_MATRIX_COLS; col++) {
      const cell = parseZhwxMatrixCell(matrix, zhwxMatrixIndex(row, col));
      rowCodes.push(
        isZhwxDk87DisplayCell(row, col, cell)
          ? zhwxMatrixCellToUiKeyCode(cell)
          : 0
      );
    }
    grid.push(rowCodes);
  }
  return grid;
}

/** DK87 虚拟键盘键宽（17 列，与 CX87-3M 矩阵对齐） */
export const TOTAL_DK87_KEY_WIDTHS: number[][] = [
  [1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 2.15, 1, 1, 1],
  [1.5, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1.65, 1, 1, 1],
  [1.75, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 2.47, 0, 0, 0],
  [2.25, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 3.05, 1, 0, 0, 0],
  [1.25, 1.25, 1.25, 0, 0, 6.52, 0, 0, 0, 0, 1.25, 1.35, 1.35, 1.39, 1, 1, 1],
];

/**
 * 固件 key_matrix_init_tab 快照（306 字节 = 6×17×3），与协议 0x16 出厂默认等价。
 * 来源：CX87-3M keyboard_user_flash.c
 */
export const ZHWX_DK87_FACTORY_MATRIX_BYTES = new Uint8Array([
  0x10, 0x00, 0x29, 0x10, 0x00, 0x00, 0x10, 0x00, 0x3a, 0x10, 0x00, 0x3b, 0x10,
  0x00, 0x3c, 0x10, 0x00, 0x3d, 0x10, 0x00, 0x3e, 0x10, 0x00, 0x3f, 0x10, 0x00,
  0x40, 0x10, 0x00, 0x41, 0x10, 0x00, 0x42, 0x10, 0x00, 0x43, 0x10, 0x00, 0x44,
  0x10, 0x00, 0x45, 0x10, 0x00, 0x46, 0x10, 0x00, 0x47, 0x10, 0x00, 0x48, 0x10,
  0x00, 0x35, 0x10, 0x00, 0x1e, 0x10, 0x00, 0x1f, 0x10, 0x00, 0x20, 0x10, 0x00,
  0x21, 0x10, 0x00, 0x22, 0x10, 0x00, 0x23, 0x10, 0x00, 0x24, 0x10, 0x00, 0x25,
  0x10, 0x00, 0x26, 0x10, 0x00, 0x27, 0x10, 0x00, 0x2d, 0x10, 0x00, 0x2e, 0x10,
  0x00, 0x2a, 0x10, 0x00, 0x49, 0x10, 0x00, 0x4a, 0x10, 0x00, 0x4b, 0x10, 0x00,
  0x2b, 0x10, 0x00, 0x14, 0x10, 0x00, 0x1a, 0x10, 0x00, 0x08, 0x10, 0x00, 0x15,
  0x10, 0x00, 0x17, 0x10, 0x00, 0x1c, 0x10, 0x00, 0x18, 0x10, 0x00, 0x0c, 0x10,
  0x00, 0x12, 0x10, 0x00, 0x13, 0x10, 0x00, 0x2f, 0x10, 0x00, 0x30, 0x10, 0x00,
  0x31, 0x10, 0x00, 0x4c, 0x10, 0x00, 0x4d, 0x10, 0x00, 0x4e, 0x10, 0x00, 0x39,
  0x10, 0x00, 0x04, 0x10, 0x00, 0x16, 0x10, 0x00, 0x07, 0x10, 0x00, 0x09, 0x10,
  0x00, 0x0a, 0x10, 0x00, 0x0b, 0x10, 0x00, 0x0d, 0x10, 0x00, 0x0e, 0x10, 0x00,
  0x0f, 0x10, 0x00, 0x33, 0x10, 0x00, 0x34, 0x10, 0x00, 0x31, 0x10, 0x00, 0x28,
  0x10, 0x00, 0x00, 0x10, 0x00, 0x00, 0x10, 0x00, 0x00, 0x10, 0x02, 0x00, 0x10,
  0x00, 0x64, 0x10, 0x00, 0x1d, 0x10, 0x00, 0x1b, 0x10, 0x00, 0x06, 0x10, 0x00,
  0x19, 0x10, 0x00, 0x05, 0x10, 0x00, 0x11, 0x10, 0x00, 0x10, 0x10, 0x00, 0x36,
  0x10, 0x00, 0x37, 0x10, 0x00, 0x38, 0x10, 0x20, 0x00, 0x10, 0x00, 0x00, 0x10,
  0x00, 0x00, 0x10, 0x00, 0x52, 0x10, 0x00, 0x00, 0x10, 0x01, 0x00, 0x10, 0x08,
  0x00, 0x10, 0x04, 0x00, 0x10, 0x00, 0x00, 0x10, 0x00, 0x00, 0x10, 0x00, 0x2c,
  0x10, 0x00, 0x00, 0x10, 0x00, 0x00, 0x10, 0x00, 0x00, 0x10, 0x00, 0x00, 0x10,
  0x40, 0x00, 0x60, 0x01, 0x00, 0x10, 0x00, 0x65, 0x10, 0x10, 0x00, 0x10, 0x00,
  0x50, 0x10, 0x00, 0x51, 0x10, 0x00, 0x4f,
]);

/** 固件 fn_key_matrix_init_tab 快照（Fn 层三级降级末级） */
export const ZHWX_DK87_FN_FACTORY_MATRIX_BYTES = new Uint8Array(
  ZHWX_DK87_FACTORY_MATRIX_BYTES
);

/** 出厂默认 UI 键码（三级降级末级；成功读到 0x17 后仍须以设备当前矩阵为准） */
export const LAYOUT_DK87_KEYCODES: number[][] = buildDk87KeycodeGridFromMatrix(
  ZHWX_DK87_FACTORY_MATRIX_BYTES
);

/** Fn 层出厂默认 UI 键码（末级兜底） */
export const LAYOUT_DK87_FN_KEYCODES: number[][] =
  buildDk87KeycodeGridFromMatrix(ZHWX_DK87_FN_FACTORY_MATRIX_BYTES);

/** 出厂快照可展示键数，用于判定协议回读是否完整 */
export const ZHWX_DK87_FACTORY_MIN_UI_KEYS = countDk87UiKeysInMatrix(
  ZHWX_DK87_FACTORY_MATRIX_BYTES
);

/** 协议回读矩阵是否可信（键数过少视为不完整/失败） */
export function isZhwxDk87MatrixUsable(matrix: Uint8Array): boolean {
  if (!matrix?.length) return false;
  const minAcceptable = Math.max(60, ZHWX_DK87_FACTORY_MIN_UI_KEYS - 12);
  return countDk87UiKeysInMatrix(matrix) >= minAcceptable;
}

/**
 * 将设备回读矩阵与出厂快照合并：出厂为底，设备侧已占用单元覆盖。
 * 2.4G 分包读不全时，避免写回稀疏矩阵把未回读键位清零。
 */
export function mergeZhwxDeviceMatrixWithFactory(
  device: Uint8Array,
  factory: Uint8Array
): Uint8Array {
  const size = Math.max(factory.length, device?.length ?? 0);
  const merged = new Uint8Array(size);
  merged.set(factory.slice(0, size), 0);
  const slotCount = Math.floor(Math.min(size, device.length) / 3);
  for (let i = 0; i < slotCount; i++) {
    const deviceCell = parseZhwxMatrixCell(device, i);
    if (!isZhwxMatrixCellOccupied(deviceCell)) continue;
    const base = i * 3;
    merged[base] = deviceCell.type;
    merged[base + 1] = deviceCell.codeL;
    merged[base + 2] = deviceCell.codeH;
  }
  return merged;
}

/** 规范化键矩阵读表：完整则原样返回，否则与出厂快照合并 */
export function normalizeZhwxKeyMatrixTab(
  raw: Uint8Array,
  factory: Uint8Array = ZHWX_DK87_FACTORY_MATRIX_BYTES
): Uint8Array {
  if (!raw?.length) return factory.slice();
  if (isZhwxDk87MatrixUsable(raw)) return raw.slice();
  return mergeZhwxDeviceMatrixWithFactory(raw, factory);
}

/** led_define_tab 后半段是否「疑似未读全」（全 0 且前半有自定义色） */
export function isZhwxLedDefineTabLikelyTruncated(led: Uint8Array): boolean {
  if (!led?.length || led.length < ZHWX_LED_DEFINE_TAB_SIZE) return true;
  let headNonZero = 0;
  let tailNonZero = 0;
  const half = Math.floor(ZHWX_LED_DEFINE_TAB_SIZE / 2);
  for (let i = 0; i < ZHWX_LED_DEFINE_TAB_SIZE; i++) {
    if ((led[i] ?? 0) === 0) continue;
    if (i < half) headNonZero++;
    else tailNonZero++;
  }
  return headNonZero > 0 && tailNonZero === 0;
}
